// Enhanced Scroll-triggered animations with better performance
export const initScrollAnimations = () => {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        
        // Add staggered animation for stagger-item elements
        if (entry.target.classList.contains('stagger-item')) {
          const parent = entry.target.parentElement;
          if (parent) {
            const siblings = Array.from(parent.children).filter(child => 
              child.classList.contains('stagger-item')
            );
            const index = siblings.indexOf(entry.target);
            entry.target.style.transitionDelay = `${index * 0.1}s`;
          }
        }
      }
    });
  }, observerOptions);

  // Observe all elements with animation classes
  const animatedElements = document.querySelectorAll(
    '.fade-in, .slide-left, .slide-right, .slide-up, .scale-in, .rotate-in, ' +
    '.stagger-item, .feature, .game-preview, .game-info, .game-screenshot, ' +
    '.social-links, .social-link, .section-title, .section-subtitle'
  );
  
  animatedElements.forEach(el => observer.observe(el));
};

// Advanced parallax scrolling with performance optimization
export const initParallax = () => {
  let ticking = false;
  const parallaxElements = document.querySelectorAll('.parallax-slow, .parallax-medium, .parallax-fast');
  
  if (parallaxElements.length === 0) return;

  const updateParallax = () => {
    const scrolled = window.pageYOffset;
    
    parallaxElements.forEach(el => {
      const speed = el.classList.contains('parallax-slow') ? 0.3 :
                   el.classList.contains('parallax-medium') ? 0.5 : 0.7;
      
      const yPos = -(scrolled * speed);
      el.style.transform = `translateY(${yPos}px)`;
    });
    
    ticking = false;
  };

  const requestTick = () => {
    if (!ticking) {
      requestAnimationFrame(updateParallax);
      ticking = true;
    }
  };

  window.addEventListener('scroll', requestTick, { passive: true });
};

// Enhanced floating elements with scroll-based movement
export const initFloatingElements = () => {
  const floatingElements = document.querySelectorAll('.floating-element');
  if (floatingElements.length === 0) return;

  let ticking = false;
  
  const updateFloatingElements = () => {
    const scrolled = window.pageYOffset;
    
    floatingElements.forEach((el, index) => {
      const speed = (index + 1) * 0.05;
      const yPos = scrolled * speed;
      const xPos = Math.sin(scrolled * 0.001 + index) * 20;
      
      el.style.transform = `translate(${xPos}px, ${yPos}px)`;
    });
    
    ticking = false;
  };

  const requestTick = () => {
    if (!ticking) {
      requestAnimationFrame(updateFloatingElements);
      ticking = true;
    }
  };

  window.addEventListener('scroll', requestTick, { passive: true });
};

// Enhanced particle system with scroll integration
export const initParticles = () => {
  const particlesContainer = document.createElement('div');
  particlesContainer.className = 'particles';
  document.body.appendChild(particlesContainer);

  const createParticle = () => {
    const particle = document.createElement('div');
    particle.className = 'particle';
    
    // Random position
    particle.style.left = Math.random() * 100 + '%';
    particle.style.top = Math.random() * 100 + '%';
    
    // Random animation delay
    particle.style.animationDelay = Math.random() * 6 + 's';
    
    particlesContainer.appendChild(particle);
    
    // Remove particle after animation
    setTimeout(() => {
      if (particle.parentNode) {
        particle.parentNode.removeChild(particle);
      }
    }, 6000);
  };

  // Create particles periodically
  setInterval(createParticle, 2000);
  
  // Create initial particles
  for (let i = 0; i < 10; i++) {
    setTimeout(createParticle, i * 200);
  }
};

// Smooth scrolling for navigation with enhanced easing
export const initSmoothScrolling = () => {
  const links = document.querySelectorAll('a[href^="#"]');
  
  links.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = link.getAttribute('href');
      const targetElement = document.querySelector(targetId);
      
      if (targetElement) {
        const targetPosition = targetElement.offsetTop - 80; // Account for navbar
        const startPosition = window.pageYOffset;
        const distance = targetPosition - startPosition;
        const duration = 1000;
        let start = null;
        
        const animation = (currentTime) => {
          if (start === null) start = currentTime;
          const timeElapsed = currentTime - start;
          const run = easeInOutCubic(timeElapsed, startPosition, distance, duration);
          window.scrollTo(0, run);
          if (timeElapsed < duration) requestAnimationFrame(animation);
        };
        
        requestAnimationFrame(animation);
      }
    });
  });
};

// Easing function for smooth scrolling
const easeInOutCubic = (t, b, c, d) => {
  t /= d / 2;
  if (t < 1) return c / 2 * t * t * t + b;
  t -= 2;
  return c / 2 * (t * t * t + 2) + b;
};

// Enhanced hero tilt with scroll integration
export const initHeroTilt = () => {
  const tiltElements = document.querySelectorAll('.hero-tilt');
  tiltElements.forEach((card) => {
    const inner = card.querySelector('.hero-card-inner');
    if (!inner) return;

    const handleMove = (e) => {
      const rect = card.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;
      const py = (e.clientY - rect.top) / rect.height;
      const rotateY = (px - 0.5) * 16; // left/right
      const rotateX = (0.5 - py) * 12; // up/down
      
      // Add scroll-based depth effect
      const scrolled = window.pageYOffset;
      const depth = Math.min(scrolled * 0.01, 0.5);
      
      inner.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(${depth * 20}px)`;
    };

    const reset = () => {
      inner.style.transform = 'rotateX(0deg) rotateY(0deg) translateZ(0px)';
    };

    card.addEventListener('mousemove', handleMove);
    card.addEventListener('mouseleave', reset);
  });
};

// Scroll-based text reveal animations
export const initTextReveal = () => {
  const textElements = document.querySelectorAll('.text-reveal');
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const text = entry.target.textContent;
        entry.target.innerHTML = '';
        
        // Create character-by-character reveal
        [...text].forEach((char, index) => {
          const span = document.createElement('span');
          span.textContent = char === ' ' ? '\u00A0' : char;
          span.style.opacity = '0';
          span.style.transform = 'translateY(20px)';
          span.style.transition = `all 0.1s ease ${index * 0.05}s`;
          entry.target.appendChild(span);
          
          setTimeout(() => {
            span.style.opacity = '1';
            span.style.transform = 'translateY(0)';
          }, 100);
        });
        
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });
  
  textElements.forEach(el => observer.observe(el));
};

// Scroll-based counter animations
export const initCounterAnimations = () => {
  const counters = document.querySelectorAll('.counter');
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const target = parseInt(entry.target.getAttribute('data-target'));
        const duration = 2000;
        const increment = target / (duration / 16);
        let current = 0;
        
        const updateCounter = () => {
          current += increment;
          if (current < target) {
            entry.target.textContent = Math.floor(current);
            requestAnimationFrame(updateCounter);
          } else {
            entry.target.textContent = target;
          }
        };
        
        updateCounter();
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });
  
  counters.forEach(counter => observer.observe(counter));
};

// Scroll-based progress bars
export const initProgressBars = () => {
  const progressBars = document.querySelectorAll('.progress-bar');
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const progress = entry.target.getAttribute('data-progress');
        const bar = entry.target.querySelector('.progress-fill');
        
        if (bar) {
          bar.style.width = '0%';
          setTimeout(() => {
            bar.style.transition = 'width 1.5s ease-out';
            bar.style.width = progress + '%';
          }, 200);
        }
        
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });
  
  progressBars.forEach(bar => observer.observe(bar));
};

// Enhanced floating stars with scroll parallax
export const initFloatingDecorations = () => {
  const stars = document.querySelectorAll('.star');
  
  if (stars.length === 0) return;
  
  let ticking = false;
  
  const updateDecorations = () => {
    const scrolled = window.pageYOffset;
    
    // Update stars with subtle parallax
    stars.forEach((star, index) => {
      const speed = (index % 3 + 1) * 0.02;
      const yPos = scrolled * speed;
      star.style.transform = `translateY(${yPos}px)`;
    });
    
    ticking = false;
  };
  
  const requestTick = () => {
    if (!ticking) {
      requestAnimationFrame(updateDecorations);
      ticking = true;
    }
  };
  
  window.addEventListener('scroll', requestTick, { passive: true });
};

// Initialize all animations with enhanced features
export const initAllAnimations = () => {
  initScrollAnimations();
  initParallax();
  initFloatingElements();
  initFloatingDecorations();
  initParticles();
  initSmoothScrolling();
  initHeroTilt();
  initTextReveal();
  initCounterAnimations();
  initProgressBars();
};

// Add staggered animations for lists and grids
export const addStaggeredAnimations = () => {
  const cards = document.querySelectorAll('.card');
  cards.forEach((card, index) => {
    card.classList.add('stagger-item');
    card.style.animationDelay = `${index * 0.1}s`;
  });
  
  const features = document.querySelectorAll('.feature');
  features.forEach((feature, index) => {
    feature.style.setProperty('--feature-index', index);
  });
  
  const listItems = document.querySelectorAll('.list-item');
  listItems.forEach((item, index) => {
    item.classList.add('stagger-item');
  });
};

// Performance optimization: Throttle scroll events
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Cleanup function for removing event listeners
export const cleanupAnimations = () => {
  // Remove scroll event listeners
  window.removeEventListener('scroll', initParallax);
  window.removeEventListener('scroll', initFloatingElements);
  window.removeEventListener('scroll', initFloatingDecorations);
  
  // Clear intervals
  if (window.particleInterval) {
    clearInterval(window.particleInterval);
  }
};
